import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { createClient } from '../../../../../../../crud/clients_api/clients';
import './NewClient.scss';

interface newClientProps {
  destination: string | undefined;
  navigate: any;
}

const NewClient: React.FC<newClientProps> = ({ destination, navigate }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  const validatePassword = (pwd: string): string[] => {
    const errors: string[] = [];
    
    if (pwd.length < 8) {
      errors.push('Al menos 8 caracteres');
    }
    if (!/[A-Z]/.test(pwd)) {
      errors.push('1 letra mayúscula');
    }
    if (!/[a-z]/.test(pwd)) {
      errors.push('1 letra minúscula');
    }
    if (!/[0-9]/.test(pwd)) {
      errors.push('1 número');
    }
    if (!/[!@#$%^&*(),.?":{}|<>\-_+=[\]\\/'`~;]/.test(pwd)) {
      errors.push('1 carácter especial');
    }
    
    return errors;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    if (newPassword.length > 0) {
      const errors = validatePassword(newPassword);
      setPasswordErrors(errors);
    } else {
      setPasswordErrors([]);
    }
  };

  const togglePasswordVisibility = () => {
    setIsPasswordShown(!isPasswordShown);
  };

  const handleRegister = async () => {
    if (!username || !email || !password || !lastName) {
      setError('Por favor completa los campos obligatorios');
      return;
    }

    // Validate password before submitting
    const pwdErrors = validatePassword(password);
    if (pwdErrors.length > 0) {
      setError('La contraseña no cumple con los requisitos');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Keep pending local_id in sessionStorage - ClientPortal will handle it after registration
      
      const response = await createClient(
        username,
        password,
        null,
        null,
        lastName,
        dob,
        null,
        email,
        phoneNumber,
        '' // middleName set to empty string
      );

      if ((response as any).status === 200) {
        localStorage.setItem('access_token', JSON.stringify((response as any).data.token));
        localStorage.setItem('client', JSON.stringify((response as any).data));
        
        // Don't remove pending_local_id here, let ClientPortal process it after navigation
        
        navigate(`/${destination || 'ClientPortal/Home'}`);
      } else {
        setError('Error al registrarse');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al registrarse. Intenta nuevamente.');
      console.error("ERROR ->", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="new-client-form">
      <div className="form-field">
        <label htmlFor="username">
          Nombre <span className="required">*</span>
        </label>
        <input
          type="text"
          id="username"
          value={username}
          placeholder="Tu nombre"
          onChange={(e) => setUsername(e.target.value)}
          disabled={isLoading}
          required
        />
      </div>

      <div className="form-field">
        <label htmlFor="lastName">
          Apellido <span className="required">*</span>
        </label>
        <input
          type="text"
          id="lastName"
          value={lastName}
          placeholder="Tu apellido"
          onChange={(e) => setLastName(e.target.value)}
          disabled={isLoading}
          required
        />
      </div>

      <div className="form-field">
        <label htmlFor="email">
          Correo Electrónico <span className="required">*</span>
        </label>
        <input
          type="email"
          id="email"
          value={email}
          placeholder="tu@email.com"
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          required
        />
      </div>

      <div className="form-field">
        <label htmlFor="password">
          Contraseña <span className="required">*</span>
        </label>
        <div className="password-input-wrapper">
          <input
            type={isPasswordShown ? "text" : "password"}
            id="password"
            value={password}
            placeholder="Mínimo 8 caracteres"
            onChange={handlePasswordChange}
            disabled={isLoading}
            required
            className={passwordErrors.length > 0 && password.length > 0 ? 'input-error' : ''}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="password-toggle-btn"
            disabled={isLoading}
            aria-label="Mostrar/Ocultar contraseña"
          >
            <FontAwesomeIcon icon={isPasswordShown ? faEyeSlash : faEye} />
          </button>
        </div>
        {passwordErrors.length > 0 && password.length > 0 && (
          <div className="password-requirements">
            <p className="requirements-title">La contraseña debe tener:</p>
            <ul className="requirements-list">
              {passwordErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="form-field">
        <label htmlFor="phone_number">Número de Teléfono</label>
        <input
          type="tel"
          id="phone_number"
          value={phoneNumber}
          placeholder="+56 9 1234 5678"
          onChange={(e) => setPhoneNumber(e.target.value)}
          disabled={isLoading}
        />
      </div>

      <div className="form-field">
        <label htmlFor="dob">Fecha de Nacimiento</label>
        <input
          type="date"
          id="dob"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          disabled={isLoading}
        />
      </div>

      {error && <p className="form-error-message">{error}</p>}

      <Button
        className="register-submit-btn"
        onClick={handleRegister}
        disabled={isLoading}
      >
        {isLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
      </Button>

      <p className="terms-text">
        Al crear una cuenta, aceptas nuestros{' '}
        <a href="/terminos">Términos y Condiciones</a>
      </p>
    </div>
  );
};

export default NewClient;
